/**
 * The one API client. Typed from the generated OpenAPI schema.
 *
 *   const data = await unwrap(api.GET('/api/recipes/{recipe_id}', { params: { path: { recipe_id } } }));
 *
 * - Sends cookies (same origin) and the CSRF header on unsafe methods.
 * - `unwrap` throws ApiError (with the server's `code`) on any non-2xx.
 * - Gate codes (not signed in, terms, password change...) are routed
 *   globally in main.tsx via onGateError; screens only handle their own codes.
 */
import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';

export type ApiErrorBody = {
  code: string;
  message: string;
  [extra: string]: unknown;
};

export class ApiError extends Error {
  status: number;
  code: string;
  body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.body = body;
  }
}

export const GATE_CODES = new Set([
  'not_authenticated',
  'account_deactivated',
  'password_change_required',
  'tos_required',
  'account_closed',
  'onboarding_required',
  'csrf_failed',
]);

export function isApiError(e: unknown, code?: string): e is ApiError {
  return e instanceof ApiError && (code === undefined || e.code === code);
}

function readCookie(name: string): string | null {
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
}

export function csrfToken(): string | null {
  return readCookie('pleach_csrf');
}

const csrf: Middleware = {
  onRequest({ request }) {
    if (!['GET', 'HEAD', 'OPTIONS'].includes(request.method)) {
      const token = csrfToken();
      if (token) request.headers.set('X-CSRF-Token', token);
    }
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: '', credentials: 'same-origin' });
api.use(csrf);

async function toApiError(response: Response, error: unknown): Promise<ApiError> {
  if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
    return new ApiError(response.status, error as ApiErrorBody);
  }
  return new ApiError(response.status, {
    code: response.status === 404 ? 'not_found' : 'http_error',
    message:
      response.status >= 500
        ? 'Something went wrong on our side. Try again in a moment.'
        : `Request failed (${response.status}).`,
  });
}

/** Resolve an openapi-fetch call to its data, or throw ApiError. */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response.ok || error !== undefined) {
    throw await toApiError(response, error);
  }
  return data as T;
}

/** Raw fetch for things openapi-fetch doesn't model well (multipart uploads,
 *  file downloads). Adds CSRF and normalises errors the same way. */
export async function rawFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method || 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = csrfToken();
    if (token) headers.set('X-CSRF-Token', token);
  }
  const response = await fetch(input, { ...init, headers, credentials: 'same-origin' });
  if (!response.ok) {
    let body: unknown = undefined;
    try {
      body = await response.clone().json();
    } catch {
      /* not JSON */
    }
    throw await toApiError(response, body);
  }
  return response;
}
